import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Loader2, RefreshCw } from "lucide-react";
import {
  useProjectGroupTimeEntries,
  useProjectTimeEntries,
} from "@/hooks/useClockify";

interface TimeTrackingCardProps {
  projectId?: string;
  projectGroupId?: string;
  emptyLabel?: string;
}

export function TimeTrackingCard({
  projectId,
  projectGroupId,
  emptyLabel = "No time entries synced for this project",
}: TimeTrackingCardProps) {
  const projectEntries = useProjectTimeEntries(projectId);
  const projectGroupEntries = useProjectGroupTimeEntries(projectGroupId);
  const {
    data: entries = [],
    isLoading,
    refetch,
  } = projectGroupId ? projectGroupEntries : projectEntries;

  // Group by person
  const byPerson = new Map<string, { name: string; seconds: number }>();
  for (const entry of entries) {
    const person = (entry as Record<string, unknown>).people as {
      first_name: string;
      last_name: string;
    } | null;
    const name = person
      ? `${person.first_name} ${person.last_name}`
      : "Unknown";
    const personId = entry.person_id || "unknown";
    const existing = byPerson.get(personId) || { name, seconds: 0 };
    existing.seconds += entry.duration_seconds || 0;
    byPerson.set(personId, existing);
  }

  const totalSeconds = entries.reduce(
    (sum, e) => sum + (e.duration_seconds || 0),
    0,
  );
  const formatHours = (seconds: number) => (seconds / 3600).toFixed(1);

  return (
    <Card data-testid="time-tracking-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Time Tracking
            </CardTitle>
            <CardDescription>Hours tracked in Clockify</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => refetch()}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm">{emptyLabel}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              {Array.from(byPerson.entries())
                .sort((a, b) => b[1].seconds - a[1].seconds)
                .map(([personId, { name, seconds }]) => (
                  <div
                    key={personId}
                    className="flex items-center justify-between text-sm"
                    data-testid={`time-tracking-person-${personId}`}
                  >
                    <span>{name}</span>
                    <span className="font-mono tabular-nums">
                      {formatHours(seconds)}h
                    </span>
                  </div>
                ))}
            </div>
            <div className="border-t pt-2 flex items-center justify-between text-sm font-medium">
              <span>Total</span>
              <span
                className="font-mono tabular-nums"
                data-testid="time-tracking-total"
              >
                {formatHours(totalSeconds)}h
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
